<script setup lang="ts">
/**
 * ProductPageTemplate - Reusable product landing page layout
 *
 * Provides semantic HTML structure with 6 named slots for different
 * product page sections. Uses Nuxt UI UContainer for consistent page width
 * and Tailwind spacing utilities for vertical alignment.
 *
 * Slots:
 * - hero: Hero section with headline, subheading, CTA (top, full-width)
 * - roiMetrics: ROI metrics display with quantified benefits (optional, below hero)
 * - problem: Problem/pain point articulation (below hero)
 * - solution: Solution overview section (middle)
 * - howItWorks: Step-by-step or feature breakdown (lower-middle)
 * - useCases: Real-world use cases or customer scenarios
 * - cta: Call-to-action section (bottom, optional secondary CTA)
 *
 * @example
 * <ProductPageTemplate>
 *   <template #hero>
 *     <HeroSection />
 *   </template>
 *   <template #problem>
 *     <ProblemBlock />
 *   </template>
 *   <!-- other slots -->
 * </ProductPageTemplate>
 */

interface ProductPageProps {
  /**
   * Optional custom background color for the page.
   * Defaults to theme neutral.50 (light background).
   */
  backgroundClass?: string;

  /**
   * Optional spacing variant.
   * Supports 'compact' (16 units), 'standard' (20 units), or 'spacious' (24 units).
   * @default 'standard'
   */
  spacingVariant?: 'compact' | 'standard' | 'spacious';
}

withDefaults(defineProps<ProductPageProps>(), {
  backgroundClass: '',
  spacingVariant: 'standard',
});

/**
 * Compute vertical spacing class based on variant.
 * Tailwind space-y utilities are applied to main wrapper.
 */
const spacingMap = {
  compact: 'space-y-16',
  standard: 'space-y-20',
  spacious: 'space-y-24',
};

const getSpacingClass = (variant: string = 'standard') => {
  return spacingMap[variant as keyof typeof spacingMap] || spacingMap.standard;
};
</script>

<template>
  <!-- Page content wrapper with semantic HTML structure -->
  <div
    role="article"
    :class="[
      'w-full min-h-screen',
      backgroundClass || 'bg-neutral-50',
    ]"
  >
    <!-- Page content container with consistent max-width -->
    <UContainer class="py-12 sm:py-16 md:py-20">
      <!-- Vertical spacing wrapper for all sections -->
      <div :class="getSpacingClass(spacingVariant)">
        <!-- Hero Section: Main headline, value prop, primary CTA -->
        <section class="w-full">
          <!--
            @slot hero - Hero section content (headline, subheading, primary CTA)
            @scope { } - No props passed to slot content
          -->
          <slot name="hero">
            <!-- Default slot content if none provided -->
            <div class="text-center">
              <p class="text-sm font-semibold text-neutral-500">
                Hero Section
              </p>
            </div>
          </slot>
        </section>

        <!-- ROI Metrics Section: Quantified value proposition with substantiation -->
        <section class="w-full">
          <!--
            @slot roiMetrics - ROI metrics display with quantified benefits
            @scope { } - No props passed to slot content
          -->
          <slot name="roiMetrics">
            <!-- Empty by default; only shown when provided -->
          </slot>
        </section>

        <!-- Problem Section: Pain point articulation, market context -->
        <section class="w-full">
          <!--
            @slot problem - Problem statement and pain point content
            @scope { } - No props passed to slot content
          -->
          <slot name="problem">
            <div class="text-center">
              <p class="text-sm font-semibold text-neutral-500">
                Problem Section
              </p>
            </div>
          </slot>
        </section>

        <!-- Solution Section: How product addresses the problem -->
        <section class="w-full">
          <!--
            @slot solution - Solution overview and key differentiators
            @scope { } - No props passed to slot content
          -->
          <slot name="solution">
            <div class="text-center">
              <p class="text-sm font-semibold text-neutral-500">
                Solution Section
              </p>
            </div>
          </slot>
        </section>

        <!-- How It Works Section: Implementation steps, features, workflow -->
        <section class="w-full">
          <!--
            @slot howItWorks - Step-by-step guide, features breakdown, or workflow visualization
            @scope { } - No props passed to slot content
          -->
          <slot name="howItWorks">
            <div class="text-center">
              <p class="text-sm font-semibold text-neutral-500">
                How It Works Section
              </p>
            </div>
          </slot>
        </section>

        <!-- Use Cases Section: Real-world scenarios, customer examples -->
        <section class="w-full">
          <!--
            @slot useCases - Use case examples, customer testimonials, or scenario cards
            @scope { } - No props passed to slot content
          -->
          <slot name="useCases">
            <div class="text-center">
              <p class="text-sm font-semibold text-neutral-500">
                Use Cases Section
              </p>
            </div>
          </slot>
        </section>

        <!-- Call-to-Action Section: Secondary CTA, sign-up, contact info -->
        <section class="w-full">
          <!--
            @slot cta - Call-to-action content (secondary CTA, email signup, or contact form)
            @scope { } - No props passed to slot content
          -->
          <slot name="cta">
            <div class="text-center">
              <p class="text-sm font-semibold text-neutral-500">
                Call-to-Action Section
              </p>
            </div>
          </slot>
        </section>
      </div>
    </UContainer>
  </div>
</template>

<style scoped>
/* Base styles for semantic sections */
section {
  display: block;
}

/* Ensure main element spans full height */
main {
  display: block;
}
</style>
